import { Classes, Dialog } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { formatMoney } from "modules/common/components/money/Money";
import { texts } from "modules/common/texts";
import {
  OrderCompletionRowType,
  OrderStagesStoreType,
  PlanOrderPaymentDetailsRowType,
} from "modules/orders-manage/models/order-stages-store";
import React, { useCallback } from "react";
import { Completion } from "./Completion";
import { PlanPayment } from "./PlanPayment";
import styles from "./OrphanItems.module.scss";

/**
 * Акты и плановые платежи, не привязанные ни к одному этапу договора.
 * Пока они существуют, договор не проходит проверку сумм при переводе в статус "Завершен",
 * а привязать их можно только через диалоги "Привязать ..." конкретного этапа.
 */
export const OrphanItems = observer(({ store, readonly, isOpen, onClose }: OrphanItemsProps) => {
  const { orphanPayments, orphanCompletions } = store;

  const removePayment = useCallback(
    (payment: PlanOrderPaymentDetailsRowType) => store.removeOrphans([payment.newGuid], []),
    [store]
  );

  const removeCompletion = useCallback(
    (completion: OrderCompletionRowType) => store.removeOrphans([], [completion.newGuid]),
    [store]
  );

  const removeAll = useCallback(
    () =>
      store.removeOrphans(
        orphanPayments.map((payment) => payment.newGuid),
        orphanCompletions.map((completion) => completion.newGuid)
      ),
    [store, orphanPayments, orphanCompletions]
  );

  const paymentLabel = useCallback(
    (payment: PlanOrderPaymentDetailsRowType) => {
      const actualSum = store.orphanPaymentActualSum(payment.newGuid);
      const base = `платеж №${payment.name} на ${formatMoney(payment.sum)}`;

      return actualSum > 0
        ? `${base} (будет отвязано фактических оплат на ${formatMoney(actualSum)})`
        : base;
    },
    [store]
  );

  return (
    <Dialog
      icon="info-sign"
      title="Нераспределенные акты и платежи"
      canEscapeKeyClose={true}
      canOutsideClickClose={false}
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "780px" }}
      backdropClassName="standard"
    >
      <div className={`${Classes.DIALOG_BODY} ${styles.body}`}>
        <div className={styles.hint}>
          Эти акты и платежи не привязаны ни к одному этапу: они не попадают в суммы по этапам и не
          дают перевести договор в статус &laquo;Завершен&raquo;. Привяжите их к этапу
          (&laquo;Привязать...&raquo; в нужном этапе) или удалите здесь.
        </div>

        <div className={styles.section}>
          <h2>Акты выполненных работ</h2>
          <div className={styles.list}>
            {orphanCompletions.length === 0 && <div className={styles.empty}>Нет</div>}
            {orphanCompletions.map((completion) => (
              <div className={styles.row} key={completion.newGuid}>
                <Completion completion={completion} />
                {!readonly && (
                  <RemoveConfirmation<OrderCompletionRowType>
                    what={(item) => `акт №${item.name} на ${formatMoney(item.sum)}`}
                    onConfirmed={removeCompletion}
                    render={({ confirmRemoving }) => (
                      <div className={styles.remove}>
                        <GeneralIcon
                          type="general-cross-small"
                          onClick={() => confirmRemoving(completion)}
                          title={texts.remove}
                        />
                      </div>
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.section}>
          <h2>Плановые платежи</h2>
          <div className={styles.list}>
            {orphanPayments.length === 0 && <div className={styles.empty}>Нет</div>}
            {orphanPayments.map((payment) => (
              <div className={styles.row} key={payment.newGuid}>
                <PlanPayment payment={payment} />
                {!readonly && (
                  <RemoveConfirmation<PlanOrderPaymentDetailsRowType>
                    what={paymentLabel}
                    onConfirmed={removePayment}
                    render={({ confirmRemoving }) => (
                      <div className={styles.remove}>
                        <GeneralIcon
                          type="general-cross-small"
                          onClick={() => confirmRemoving(payment)}
                          title={texts.remove}
                        />
                      </div>
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {!readonly && store.orphansCount > 0 && (
        <div className={Classes.DIALOG_FOOTER}>
          <RemoveConfirmation<boolean>
            what={() => `все нераспределенные записи (${store.orphansCount})`}
            onConfirmed={removeAll}
            render={({ confirmRemoving }) => (
              <PlanrButton
                type="secondary"
                icon="general-trash"
                size="small"
                onClick={() => confirmRemoving(true)}
              >
                Удалить все
              </PlanrButton>
            )}
          />
        </div>
      )}
    </Dialog>
  );
});

interface OrphanItemsProps {
  store: OrderStagesStoreType;
  readonly?: boolean;
  isOpen: boolean;
  onClose: () => void;
}
